import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Quote, Heart, Bookmark, MessageCircle, BookOpen } from "lucide-react";
import { GlossaryText } from "@/components/GlossaryText";
import { useAuth } from "@/context/AuthContext";

export function PostCard({ post, glossary, onDelete, showActions = false, liked = false, saved = false, likeCount = 0, onLike, onSave, bookTitle, showFollow = false, isFollowing = false, onFollow }) {
  const { user } = useAuth();
  const [revealed, setRevealed] = useState(!post.spoiler_mi);
  const canDelete = user && (user.id === post.user_id || ["admin", "moderator"].includes(user.role));
  const tarih = post.olusturma_tarihi ? new Date(post.olusturma_tarihi).toLocaleDateString("tr-TR") : "";

  const turColor = { Alıntı: "text-gold", Abstract: "text-sage", Yorum: "text-terra" }[post.tur] || "text-terra";

  return (
    <div className="border border-line rounded-lg p-5 bg-surface fade-up" data-testid={`post-${post.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-surface-alt flex items-center justify-center overflow-hidden border border-line">
            {post.yazar_resmi ? <img src={post.yazar_resmi} alt="" className="w-full h-full object-cover" /> :
              <span className="font-serif text-terra text-lg">{(post.yazar_adi || "?")[0]}</span>}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/uye/${post.user_id}`} className="text-sm font-medium text-ink hover:text-terra">{post.yazar_adi}</Link>
              {showFollow && !post.is_self && (
                <button onClick={onFollow} data-testid={`follow-${post.user_id}`}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${isFollowing ? "border-line text-muted-foreground hover:text-destructive" : "border-terra text-terra hover:bg-terra hover:text-white"}`}>
                  {isFollowing ? "Takiptesin ✓" : "+ Takip Et"}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{tarih}</span>
              {bookTitle && post.book_id && (
                <Link to={`/kitap/${post.book_id}`} data-testid={`post-book-${post.id}`}
                  className="inline-flex items-center gap-1 text-terra font-medium hover:underline truncate max-w-[180px] sm:max-w-[260px]">
                  <BookOpen size={12} /> {bookTitle}
                </Link>
              )}
              {post.sayfa ? <span className="text-muted-foreground">· s. {post.sayfa}</span> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs uppercase tracking-[0.15em] font-bold ${turColor}`}>{post.tur}</span>
          {canDelete && (
            <button data-testid={`delete-post-${post.id}`} onClick={() => onDelete(post.id)}
              className="text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      {post.tur === "Alıntı" ? (
        <div className="relative pl-8">
          <Quote className="absolute left-0 top-0 text-terra/30" size={22} />
          <div className={`font-serif text-xl italic text-ink leading-relaxed ${post.spoiler_mi && !revealed ? "spoiler" : ""}`}
            onClick={() => !revealed && setRevealed(true)}>
            <GlossaryText text={post.icerik} glossary={glossary} />
          </div>
        </div>
      ) : (
        <div className={`text-ink-2 leading-relaxed ${post.spoiler_mi && !revealed ? "spoiler" : ""}`}
          onClick={() => !revealed && setRevealed(true)}>
          <GlossaryText text={post.icerik} glossary={glossary} />
        </div>
      )}
      {post.spoiler_mi && !revealed && (
        <p className="text-xs text-terra mt-2">Spoiler içerir — görmek için tıklayın</p>
      )}
      {showActions && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-line/60">
          <button onClick={onLike} data-testid={`like-${post.id}`}
            className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? "text-terra" : "text-muted-foreground hover:text-terra"}`}>
            <Heart size={16} fill={liked ? "#A24936" : "none"} /> {likeCount > 0 ? likeCount : ""} Beğen
          </button>
          {post.book_id && (
            <Link to={`/kitap/${post.book_id}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-terra">
              <MessageCircle size={16} /> Yorum
            </Link>
          )}
          <button onClick={onSave} data-testid={`save-${post.id}`}
            className={`flex items-center gap-1.5 text-sm transition-colors ${saved ? "text-gold" : "text-muted-foreground hover:text-gold"}`}>
            <Bookmark size={16} fill={saved ? "#D4AF37" : "none"} /> {saved ? "Kaydedildi" : "Kaydet"}
          </button>
        </div>
      )}
    </div>
  );
}
